import { useState, useEffect } from "react";
import api from "../../../../../../api/api";

const useCommercialData = (id) => {
  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [containerTableData, setContainerTableData] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [chargeCodeOptions, setChargeCodeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [loadingChargeCodes, setLoadingChargeCodes] = useState(false);
  const [initialVendors, setInitialVendors] = useState([]);
  const [initialPurchases, setInitialPurchases] = useState([]);
  const [portOptions, setPortOptions] = useState([]);
  const [loadingPorts, setLoadingPorts] = useState(false);

  // Fetch ports
  useEffect(() => {
    const fetchPorts = async () => {
      try {
        setLoadingPorts(true);
        const response = await api.get("/ports");
        const options = (response.data || []).map((p) => ({
          value: p.Port_id,
          label: p.Port_names,
        }));
        setPortOptions(options);
      } catch (err) {
        console.error("Failed to fetch ports:", err);
      } finally {
        setLoadingPorts(false);
      }
    };
    fetchPorts();
  }, []);

  // Fetch charge codes
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        setLoadingCurrencies(true);
        const response = await api.get("/exchange_rate");
        const formatted = response.data.map((item) => ({
          value: item.country_code,
          label: item.country_code,
        }));
        setCurrencyOptions(formatted);
      } catch (err) {
        console.error("Failed to fetch exchange rates:", err);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    fetchExchangeRates();
  }, []);

  // Fetch charge codes
  useEffect(() => {
    const fetchChargeCodes = async () => {
      try {
        setLoadingChargeCodes(true);
        const response = await api.get("/charge_code/options");
        setChargeCodeOptions(response.data);
      } catch (err) {
        console.error("Failed to fetch charge codes:", err);
      } finally {
        setLoadingChargeCodes(false);
      }
    };
    fetchChargeCodes();
  }, []);

  // Fetch vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        setLoadingVendors(true);
        const response = await api.get("/master-data/vendor-master");
        const vendors = (response.data.vendors || []).map((v) => ({
          value: v.vendor_id,
          label: v.name,
          ...v,
        }));
        setVendorOptions(vendors);
      } catch (err) {
        console.error("Failed to fetch vendors:", err);
      } finally {
        setLoadingVendors(false);
      }
    };
    fetchVendors();
  }, []);

  // Fetch enquiry data
  useEffect(() => {
    const fetchEnquiry = async () => {
      try {
        const response = await api.get(
          `/master-data/container-status-report/${id}`,
        );
        const enqData = response.data.enquiry;

        // Pull the full Tax-Invoice field set for this enquiry
        let invoiceFields = {};
        // Containers from viewEnquiry (primary source for the container dropdown)
        let enquiryContainers = [];
        try {
          const invRes = await api.get(`/enquiry/viewEnquiry/${id}`);
          const e = invRes.data || {};
          invoiceFields = {
            job_number: e.job_number || "",
            inv_number: e.inv_number || "",
            bl_number: e.bl_number || "",
            other_reference: e.other_reference || "",
            invoice_date: e.invoice_date || "",
            billing_party: e.billing_party || "",
            shipper: e.first_shipper_name || "",
            consignee: e.first_consignee_name || "",
            notify_party1: e.notify_party1 || "",
            notify_party2: e.notify_party2 || "",
            pol: e.pol || "",
            pod: e.pod || "",
            place_of_receipt: e.place_of_receipt || "",
            final_delivery: e.final_delivery || "",
            final_port_of_delivery: e.final_port_of_delivery || "",
            transshipment_hub: e.transshipment_hub || "",
            shipment_segment: e.shipment_segment || "",
            incoterms: e.incoterm || "",
            commodity_name: e.hs_description || "",
            freight_terms: e.freight_terms || "",
            gross_weight: e.gross_weight || "",
            number_of_packages: e.number_of_packages || "",
          };
          enquiryContainers = Array.isArray(e.containerDetails)
            ? e.containerDetails
            : [];
        } catch (err) {
          console.error("Failed to load invoice fields:", err);
        }

        setEnquiry({ ...enqData, ...invoiceFields });
        // Prefer containers from viewEnquiry; fall back to the container-status
        // report response when viewEnquiry has none.
        const sourceContainers =
          enquiryContainers.length > 0
            ? enquiryContainers
            : enqData.containerDetails || [];
        setContainerTableData(
          sourceContainers.map((c, i) => ({
            container_id: c.container_id ?? i,
            container_type: c.container_type,
            container_number: c.container_number || "",
            seal_number: c.seal_number || "",
            container_weight: c.container_weight,
            container_length: c.container_length,
            container_breadth: c.container_breadth,
            container_height: c.container_height,
            container_temperature: c.container_temperature,
          })),
        );

        // Build container ID -> label lookup from this enquiry's containers
        const containerLookup = {};
        sourceContainers.forEach((c, i) => {
          const cid = c.container_id ?? i;
          const label = c.container_number
            ? `${c.container_number}${c.container_type ? " (" + c.container_type + ")" : ""}`
            : `Container ${i + 1}${c.container_type ? " (" + c.container_type + ")" : ""}`;
          containerLookup[cid] = label;
        });

        // Resolve job_number + shipment context from billing transactions by enq_id
        let txn = null;
        try {
          const txnRes = await api.get("/billing/transactions");
          txn =
            (txnRes.data?.transactions || []).find((t) => t.enq_id === id) ||
            null;
        } catch (err) {
          console.error(
            "Failed to resolve job context from transactions:",
            err,
          );
        }

        // Build vendor_id -> tds_percentage lookup from vendor master
        const tdsLookup = {};
        try {
          const vendorRes = await api.get("/master-data/vendor-master");
          (vendorRes.data?.vendors || []).forEach((v) => {
            tdsLookup[v.vendor_id] = v.tds_percentage ?? 0;
          });
        } catch (err) {
          console.error("Failed to fetch vendor master for TDS:", err);
        }

        // Always resolve ALL purchases for this enquiry (bridge enq_id -> invoices)
        try {
          const entriesRes = await api.get(`/purchase/by-enquiry/${id}`);
          const entries = entriesRes.data.entries || [];

          if (entries.length > 0) {
            // Fetch full detail for every invoice in parallel
            const detailResults = await Promise.all(
              entries.map((e) =>
                api
                  .get(`/purchase/${e.purchase_invoice_id}`)
                  .then((r) => r.data)
                  .catch((err) => {
                    console.error(
                      `Failed to load purchase ${e.purchase_invoice_id}:`,
                      err,
                    );
                    return null;
                  }),
              ),
            );

            // Use the first successful invoice for the header/Sales context
            const firstPurchase = detailResults.find((p) => p && p.success);
            if (firstPurchase || txn) {
              setEnquiry((prev) => ({
                ...(prev || {}),
                purchase_invoice_id: firstPurchase?.purchase_invoice_id,
                job_number:
                  txn?.job_number ??
                  firstPurchase?.job_number ??
                  prev?.job_number ??
                  "",
                po_number: firstPurchase?.po_number ?? prev?.po_number ?? "",
                purchase_invoice_number:
                  firstPurchase?.purchase_invoice_number ?? "",
                purchase_invoice_date:
                  firstPurchase?.purchase_invoice_date ?? "",
                actual_shipper:
                  firstPurchase?.actual_shipper ??
                  txn?.first_shipper_name ??
                  "",
                actual_consignee:
                  firstPurchase?.actual_consignee ??
                  txn?.first_consignee_name ??
                  "",
                house_bl_number: firstPurchase?.house_bl_number ?? "",
                pol: firstPurchase?.pol ?? txn?.pol ?? prev?.pol ?? "",
                pod: firstPurchase?.pod ?? txn?.pod ?? prev?.pod ?? "",
                notify_party:
                  firstPurchase?.notify_party ?? txn?.notify_party_1 ?? "",
                gross_wt: firstPurchase?.gross_wt ?? txn?.gross_weight ?? "",
                number_of_packages: firstPurchase?.number_of_packages ?? "",
                shipment_segment:
                  firstPurchase?.shipment_segment ??
                  txn?.shipment_segment ??
                  "",
                incoterms: firstPurchase?.incoterms ?? txn?.incoterm ?? "",
                freight_terms:
                  firstPurchase?.freight_terms ?? txn?.freight_terms ?? "",
                container_count: firstPurchase?.container_count ?? 0,
              }));
            }

            // Flatten every invoice's vendorEntries into table rows
            const flattened = [];
            detailResults.forEach((purchase) => {
              if (!purchase || !purchase.success) return;
              (purchase.vendorEntries || []).forEach((v, idx) => {
                flattened.push({
                  id:
                    v.vendor_entry_id ||
                    v.vendor_id ||
                    `PUR-${purchase.purchase_invoice_id}-${idx}`,
                  purchase_invoice_id: purchase.purchase_invoice_id,
                  purchase_invoice_number:
                    purchase.purchase_invoice_number || "",
                  purchase_invoice_date: purchase.purchase_invoice_date || "",
                  job_number: txn?.job_number || purchase.job_number || "",
                  vendor_id: v.vendor_id,
                  tds_percentage: tdsLookup[v.vendor_id] ?? 0,
                  tds_deduct: (tdsLookup[v.vendor_id] ?? 0) > 0,
                  vendor_name: v.vendor_name || "",
                  vendor_location: v.vendor_location || "",
                  vendor_total: v.vendor_total ?? 0,
                  gst_number: v.party_gstin || "",
                  pan_number: v.pan_number || "",
                  voucher_invoice_number: v.voucher_invoice_number || "",
                  vendor_invoice_date: v.voucher_invoice_date || "",
                  vb_number: v.vb_number || "",
                  vendor_job_number: v.vendor_job_number || "",
                  master_shipper: v.shipper || "",
                  master_consignee: v.consignee || "",
                  master_bl_number: v.mbl_number || "",
                  vessel_name: v.vessel_name || "",
                  vessel_number: v.vessel_number || "",
                  voyage_number: v.voyage_number || "",
                  estimated_date: v.etd_date || "",
                  cut_off_date: v.cut_off_date || "",
                  items: (v.charges || []).map((c) => ({
                    charge_code: c.charge_code || "",
                    sac_code: c.sac_code || "",
                    currency: c.currency || "",
                    rate: c.rate ?? "",
                    quantity: c.quantity ?? "",
                    taxable_amount: c.taxable_amount ?? "",
                    gst_applicable: c.gst_applicable || "Yes",
                    gst_percent: c.gst_percentage ?? "",
                    gst_amount: c.gst_amount ?? "",
                    containers: (c.applicableContainers || [])
                      .map((cid) => containerLookup[cid] || String(cid))
                      .join(", "),
                  })),
                });
              });
            });
            setInitialPurchases(flattened);
          } else {
            setInitialPurchases([]);
          }
        } catch (err) {
          console.error("Failed to load purchases for enquiry:", err);
          setInitialPurchases([]);
        }
      } catch (error) {
        console.error("Error fetching enquiry:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEnquiry();
  }, [id]);

  // Fetch GST for a charge code
  const fetchGSTForChargeCode = async (
    charge_code,
    vendorId,
    index,
    setVendors,
  ) => {
    if (!charge_code) return;
    try {
      const response = await api.get(`/charge_code/gst/${charge_code}`);
      const gstPercentage = response.data.tax_percentage || 0;

      if (index === "input") {
        setVendors((prev) =>
          prev.map((v) => {
            if (v.id !== vendorId) return v;
            const updatedInput = { ...v.inputChargeRow, gst: gstPercentage };
            const amt = parseFloat(updatedInput.taxable_amount) || 0;
            const qty = parseFloat(updatedInput.quantity) || 0;
            const exchange = parseFloat(updatedInput.exchange_rate) || 1;
            const taxAmount = amt * qty * exchange;
            const gstAmount = (taxAmount * gstPercentage) / 100;
            const total = taxAmount + gstAmount;
            updatedInput.tax = Number(taxAmount.toFixed(2));
            updatedInput.gst_amount = Number(gstAmount.toFixed(2));
            updatedInput.cgst = Number((gstAmount / 2).toFixed(2));
            updatedInput.sgst = Number((gstAmount / 2).toFixed(2));
            updatedInput.total = Number(total.toFixed(2));
            return { ...v, inputChargeRow: updatedInput };
          }),
        );
      } else {
        setVendors((prev) =>
          prev.map((v) => {
            if (v.id !== vendorId) return v;
            const updated = [...v.chargeRows];
            updated[index].gst = gstPercentage;
            const amount = parseFloat(updated[index].taxable_amount) || 0;
            const qty = parseFloat(updated[index].quantity) || 0;
            const exchange = parseFloat(updated[index].exchange_rate) || 1;
            const taxAmount = amount * qty * exchange;
            const gstAmount = (taxAmount * gstPercentage) / 100;
            const total = taxAmount + gstAmount;
            updated[index].gst_amount = Number(gstAmount.toFixed(2));
            updated[index].cgst = Number((gstAmount / 2).toFixed(2));
            updated[index].sgst = Number((gstAmount / 2).toFixed(2));
            updated[index].tax = Number(taxAmount.toFixed(2));
            updated[index].total = Number(total.toFixed(2));
            return { ...v, chargeRows: updated };
          }),
        );
      }
    } catch (err) {
      console.error("Failed to fetch GST for charge code:", err);
    }
  };

  // Container options derived from containerTableData
  const containerOptions = containerTableData.map((c, i) => ({
    value: c.container_id ?? i,
    label: c.container_number
      ? `${c.container_number}${c.container_type ? " (" + c.container_type + ")" : ""}`
      : `Container ${i + 1}${c.container_type ? " (" + c.container_type + ")" : ""}`,
  }));

  return {
    enquiry,
    loading,
    containerTableData,
    containerOptions,
    vendorOptions,
    loadingVendors,
    chargeCodeOptions,
    currencyOptions,
    loadingCurrencies,
    loadingChargeCodes,
    initialVendors,
    initialPurchases,
    portOptions,
    loadingPorts,
    fetchGSTForChargeCode,
  };
};

export default useCommercialData;
